import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Col, Divider, Row, Space, Spin } from 'antd'
import { isEmpty } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  CategoryBreadcrumbs,
  HistoryLink,
} from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import {
  APP_ROUTES,
  EVENT_METADATA_TYPES,
  EVENT_TYPES,
  METADATA_TYPES,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useAllergensQuery,
  useDocumentTitle,
  useIngredientsQuery,
  useUpdatedIngredientSubscription,
} from '../../../common/hooks'

import { renderDietEvent } from '../../helpers'
import { useDietQuery } from '../../hooks'
import {
  DietExcludedAllergensDetailsForm,
  DietExcludedIngredientsDetailsForm,
  DietSimpleDetailsForm,
} from '../forms'
import DietImageDetailsForm from '../forms/diet-image-details-form'

const DietDetailsPage = () => {
  const { id } = useParams()

  const isDietPageDirty = useSelector(
    store =>
      store.diets.diet.isExcludedIngredientsDirty ||
      store.diets.diet.isExcludedAllergensDirty ||
      store.diets.diet.isSimpleDetailsDirty,
  )

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    data,
    refetch,
  } = useDietQuery(id)

  useUpdatedIngredientSubscription(
    id,
    isDietPageDirty,
    METADATA_TYPES.DIETS.value,
    refetch,
  )

  const {
    data: ingredients,
    isLoading: isLoadingIngredients,
    error: ingredientsError,
  } = useIngredientsQuery()

  const {
    data: allergens,
    options: allergenOptions,
    isLoading: isLoadingAllergens,
    error: allergensError,
  } = useAllergensQuery()

  useDocumentTitle(`${data?.name || 'Loading Diet...'}`)

  useEffect(() => {
    if (fetchError || ingredientsError || allergensError) {
      showErrorNotification({
        title: 'Diet error',
        description: fetchError || ingredientsError || allergensError,
      })
    }
  }, [allergensError, fetchError, ingredientsError])

  return isFetchLoading ||
    isLoadingIngredients ||
    isLoadingAllergens ||
    isEmpty(data) ? (
    <Spin />
  ) : (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <CategoryBreadcrumbs
              idPath={data.node.idPath}
              namePath={data.node.namePath}
              rootAppRoute={APP_ROUTES.DIETS}
              categoryAppRoute={APP_ROUTES.DIET_CATEGORIES}
            />
          </Col>

          <Space>
            <UpdateDataText
              updatedAt={data.updatedAt}
              updatedBy={data.updatedBy?.name}
            />

            <HistoryLink
              renderEvent={renderDietEvent}
              entityId={id}
              eventTypes={[EVENT_TYPES.UPDATE]}
              eventMetadataTypes={[EVENT_METADATA_TYPES.DIET]}
            />
          </Space>
        </Row>
      </StyledPageSubTitle>
      <DietSimpleDetailsForm diet={data} maxPosition={data.maxPosition} />
      <Divider />
      <DietImageDetailsForm diet={data} />
      {!isEmpty(allergenOptions) && (
        <DietExcludedAllergensDetailsForm
          dietId={data.id}
          excludedAllergens={data.excludedAllergens}
          allergenOptions={allergenOptions}
        />
      )}
      <Divider />
      {!isEmpty(ingredients) && !isEmpty(allergens) && (
        <DietExcludedIngredientsDetailsForm
          dietId={data.id}
          allergens={allergens}
          excludedAllergens={data.excludedAllergens}
          excludedIngredients={data.excludedIngredients}
          ingredientTreeData={ingredients}
        />
      )}
    </>
  )
}

export default DietDetailsPage
